import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { i18nGlobal } from '../i18n'
import type { Notification } from '../types'

export const useNotificationStore = defineStore('notifications', () => {
  // State
  const notifications = ref<Notification[]>([])
  const maxNotifications = 5
  const defaultDuration = 5000 // 5 seconds

  // Getters
  const hasNotifications = computed(() => notifications.value.length > 0)
  
  const latestNotification = computed(() => 
    notifications.value[notifications.value.length - 1] || null
  )

  // Actions
  function addNotification(notification: Omit<Notification, 'id'>) {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const duration = notification.duration ?? defaultDuration

    const newNotification: Notification = {
      id,
      ...notification,
      duration,
    }

    // Limit number of notifications
    if (notifications.value.length >= maxNotifications) {
      notifications.value.shift()
    }

    notifications.value.push(newNotification)

    // Announce to screen readers
    announceNotification(newNotification)

    // Auto-remove after duration (except for errors which require manual dismissal)
    if (notification.type !== 'error' && duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }

    return id
  }

  function removeNotification(id: string) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index !== -1) {
      notifications.value.splice(index, 1)
    }
  }

  function clearAll() {
    notifications.value = []
  }

  // Convenience methods
  function success(title: string, message?: string) {
    return addNotification({ type: 'success', title, message })
  }

  function error(title: string, message?: string) {
    return addNotification({ type: 'error', title, message, duration: 0 })
  }

  function warning(title: string, message?: string) {
    return addNotification({ type: 'warning', title, message })
  }

  function info(title: string, message?: string) {
    return addNotification({ type: 'info', title, message })
  }

  // Announce notification to screen readers
  function announceNotification(notification: Notification) {
    const typeLabel = i18nGlobal.t(`a11y.notificationTypes.${notification.type}`)

    const fullMessage = `${typeLabel}: ${notification.title}${notification.message ? '. ' + notification.message : ''}`

    // Use assertive for errors, polite for others
    if (notification.type === 'error') {
      if ((window as any).__announceAssertive) {
        (window as any).__announceAssertive(fullMessage)
      }
    } else {
      if ((window as any).__announcePolite) {
        (window as any).__announcePolite(fullMessage)
      }
    }
  }

  return {
    // State
    notifications,

    // Getters
    hasNotifications,
    latestNotification,

    // Actions
    addNotification,
    removeNotification,
    clearAll,
    success,
    error,
    warning,
    info,
  }
})
